import { type JSX, cloneElement, useMemo } from 'react';

import { useListingAppContext } from '@/microfrontends/listing/shared/context/ListingAppContext';
import { useCardOfferDataContext } from '@/microfrontends/listing/shared/context/selectors/cardData';

import { memoWithDisplayName } from '@/packages/react-utils/memoWithDisplayName';
import { WrapElementsSpacer } from '@/packages/shared-ui';

import {
  BestPlaceAnalyticsLabelContainer,
  CoworkingsBenefitsLabelContainer,
  DepositPropertyLabel,
  FromOwnerLabelContainer,
  GoodPriceLabelContainer,
  UniqueForCianLabelContainer,
  WithAuctionLabelContainer,
} from '../../common/LabelContainer';

import { type PublisherLabelTypes } from './types';

/**
 * @description Данная конструкция нужна для того, что бы логировать случаи, когда бекенд присылает лейбл, но фронтенд
 *   о нём не знает
 */
const unknownLoggedLabels = new Set<PublisherLabelTypes>();

export const PublisherLabelsContainer = memoWithDisplayName(() => {
  const { logger, config } = useListingAppContext();
  const {
    extendedProperties: { labels },
  } = useCardOfferDataContext();

  /**
   * @todo Удалить фича-тогл
   * @description Данный функционал появился в задаче CD-187914, будет удалён в задаче CD-188067
   */
  const electronicTradingEnabled = config.getStrict<boolean>('search-frontend.ElectronicTrading.Enabled');

  const labelsViews = useMemo(() => {
    return labels.reduce((accumulator, label) => {
      const labelView = ((): JSX.Element | undefined => {
        switch (label) {
          case 'goodPrice':
            return <GoodPriceLabelContainer />;
          case 'uniqueForCian':
            return <UniqueForCianLabelContainer />;
          case 'fromOwner':
            return <FromOwnerLabelContainer />;
          case 'coworkingBenefits':
            return <CoworkingsBenefitsLabelContainer />;
          case 'bestPlaceAnalytics':
            return <BestPlaceAnalyticsLabelContainer />;
        }

        /**
         * @todo Удалить if-блок и ненужный "return;" при удалении фича-тогла
         * @description Данный функционал появился в задаче CD-187914, будет удалён в задаче CD-188067
         */
        if (electronicTradingEnabled) {
          switch (label) {
            case 'depositProperty':
              return <DepositPropertyLabel />;
            case 'withAuction':
              return <WithAuctionLabelContainer />;
          }
        }

        return;
      })();

      if (labelView) {
        accumulator.push(cloneElement(labelView, { key: label }));
      } else if (!unknownLoggedLabels.has(label)) {
        logger.warning(
          `Не реализован лейбл публиканта, нужно добавить бейдж с типом: ${label} или наоборот удалить его так как визуала под этот тип нет`,
          { domain: '@/shared/components/PublisherLabels/PublisherLabelsContainer.tsx' },
        );

        unknownLoggedLabels.add(label);
      }

      return accumulator;
    }, Array.of<JSX.Element>());
  }, [logger, electronicTradingEnabled]);

  return (
    <WrapElementsSpacer
      containerTestId="publisher-labels.container"
      horizontalSpace={8}
      itemTestId="publisher-labels.label"
      verticalSpace={8}
    >
      {labelsViews}
    </WrapElementsSpacer>
  );
}, 'PublisherBadgesContainer');
